import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import Search from "@mui/icons-material/Search";
import React, { useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import MetaTemplateModal from "../../components/MetaTemplateModal";
import SendTemplateModal from "../../components/SendTemplateModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import ChipTemplates from "./ChipTemplates";

const reducer = (state, action) => {
  if (action.type === "LOAD_TEMPLATES") {
    return action.payload;
  }
  if (action.type === "UPSERT_TEMPLATE") {
    const template = action.payload;
    const idx = state.findIndex((t) => t.id === template.id);
    if (idx !== -1) {
      state[idx] = template;
      return [...state];
    }
    return [template, ...state];
  }
  if (action.type === "DELETE_TEMPLATE") {
    return state.filter((t) => t.id !== +action.payload);
  }
  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  margin: theme.spacing(1),
  overflowY: "scroll",
  ...theme.scrollbarStyles,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}));

const STATUS_COLOR = {
  APPROVED: "success",
  PENDING: "warning",
  REJECTED: "error",
  PAUSED: "default",
  DISABLED: "default",
  LOCAL: "info",
};

const STATUS_LABEL = {
  APPROVED: "Aprovado",
  PENDING: "Em análise",
  REJECTED: "Reprovado",
  PAUSED: "Pausado",
  DISABLED: "Desativado",
  LOCAL: "Local",
};

const CATEGORY_LABEL = {
  MARKETING: "Marketing",
  UTILITY: "Utilidade",
  AUTHENTICATION: "Autenticação",
};

const OfficialTemplates = () => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [templates, dispatch] = useReducer(reducer, []);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [sendTemplate, setSendTemplate] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/metaTemplates", {
        params: { searchParam },
      });
      dispatch({ type: "LOAD_TEMPLATES", payload: data });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchTemplates();
    }, 400);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("metaTemplate", (data) => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPSERT_TEMPLATE", payload: data.template });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TEMPLATE", payload: data.templateId });
      }
      if (data.action === "sync") {
        fetchTemplates();
      }
    });
    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const { data } = await api.post("/metaTemplates/sync");
      dispatch({ type: "LOAD_TEMPLATES", payload: data });
      toast.success("Templates sincronizados com a Meta.");
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenModal = () => {
    setSelectedTemplate(null);
    setModalOpen(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedTemplate(null);
    setModalOpen(false);
  };

  const handleDelete = async (templateId) => {
    try {
      await api.delete(`/metaTemplates/${templateId}`);
      dispatch({ type: "DELETE_TEMPLATE", payload: templateId });
      toast.success("Template excluído.");
    } catch (err) {
      toastError(err);
    }
    setDeletingTemplate(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingTemplate
            ? `Excluir o template "${deletingTemplate.name}"?`
            : "Excluir template"
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deletingTemplate.id)}
      >
        Esta ação remove o template na Meta e nesta plataforma. Não pode ser
        desfeita.
      </ConfirmationModal>

      <MetaTemplateModal
        open={modalOpen}
        onClose={handleCloseModal}
        templateId={selectedTemplate && selectedTemplate.id}
      />

      <SendTemplateModal
        open={Boolean(sendTemplate)}
        onClose={() => setSendTemplate(null)}
        template={sendTemplate}
      />

      <MainHeader>
        <Title>
          Templates Oficiais (WABA){" "}
          {templates.length > 0 ? `(${templates.length})` : ""}
        </Title>
        <MainHeaderButtonsWrapper>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              width: "100%",
              gap: 1,
            }}
          >
            <TextField
              placeholder="Pesquisar pelo nome"
              type="search"
              size="small"
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="secondary" />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { height: 40 } }}
            />
            <Tooltip title="Sincronizar status com a Meta">
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSync}
                  disabled={syncing}
                  sx={{ height: 40, minWidth: 40 }}
                >
                  {syncing ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SyncIcon />
                  )}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Novo template">
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenModal}
                sx={{ height: 40, minWidth: 40 }}
              >
                <AddCircleOutline />
              </Button>
            </Tooltip>
          </Box>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <MainPaper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell align="center">Idioma</TableCell>
              <TableCell align="center">Categoria</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell>{template.name}</TableCell>
                <TableCell align="center">{template.language}</TableCell>
                <TableCell align="center">
                  {CATEGORY_LABEL[template.category] || template.category}
                </TableCell>
                <TableCell align="center">
                  <Tooltip
                    title={template.rejectedReason || ""}
                    disableHoverListener={!template.rejectedReason}
                  >
                    <Chip
                      size="small"
                      label={STATUS_LABEL[template.status] || template.status}
                      color={STATUS_COLOR[template.status] || "default"}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Enviar template">
                    <span>
                      <IconButton
                        size="small"
                        disabled={template.status !== "APPROVED"}
                        onClick={() => setSendTemplate(template)}
                      >
                        <SendIcon
                          color={
                            template.status === "APPROVED"
                              ? "primary"
                              : "disabled"
                          }
                          fontSize="small"
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton size="small" onClick={() => handleEdit(template)}>
                    <Edit color="info" fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDeletingTemplate(template);
                      setConfirmModalOpen(true);
                    }}
                  >
                    <DeleteOutline color="error" fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={5} />}
          </TableBody>
        </Table>
      </MainPaper>
    </MainContainer>
  );
};

// Templates em duas abas: oficiais (Meta, com aprovação) e não oficiais (chip).
const ABA_KEY = "templatesAba";
const MetaTemplates = () => {
  const [aba, setAba] = useState(() => {
    try {
      return localStorage.getItem(ABA_KEY) || "oficial";
    } catch {
      return "oficial";
    }
  });
  const trocar = (_, v) => {
    setAba(v);
    try {
      localStorage.setItem(ABA_KEY, v);
    } catch {}
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Tabs value={aba} onChange={trocar} sx={{ px: 2, minHeight: 40 }}>
        <Tab value="oficial" label="Oficiais (Meta)" sx={{ minHeight: 40 }} />
        <Tab value="chip" label="Não oficiais (chip)" sx={{ minHeight: 40 }} />
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {aba === "oficial" ? <OfficialTemplates /> : <ChipTemplates />}
      </Box>
    </Box>
  );
};

export default MetaTemplates;
